import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { isEqual } from "lodash";
import { Value } from "baseui/select";
import { UserRoles } from "@store/user/types";
import {
  selectCurrentPropertyId,
  selectCurrentRole,
  selectSelectedUser,
} from "@store/user/userSelectors";
import useAppDispatch from "@hooks/useAppDispatch";
import { Form, getMostImportantRole } from "@helpers";
import { createUser, deleteUser, updateUser } from "@store/user/userThunk";
import Select from "@components/base/select";
import Input from "@components/base/input";
import Notification from "@components/base/notification";
import { css } from "@emotion/react";
import theme from "@styles/theme";
import Modal from "@components/base/modal";
import Button from "@components/base/button";
import {
  generateButtonColors,
  overrideButtonStyles,
} from "@components/base/button/overrides";
import { useTranslation } from "react-i18next";
import { HiOutlineInformationCircle } from "react-icons/hi";
import PopOver from "@components/base/popover";
import { PLACEMENT } from "baseui/popover";

interface MemberFormProps {
  roleOptions: {
    label: string;
    extraLabel?: string;
    value: UserRoles | undefined;
    leftComponent?: JSX.Element | null | boolean;
  }[];
}

const buildSchema = (emailChanged: boolean) =>
  yup.object().shape({
    name: yup.string().required(),
    role: yup.string().required(),
    email: yup.string().when("role", {
      is: (role: FormData["role"]) => role && role !== UserRoles.HK_MEMBER,
      then: (schema) => schema.email().required(),
    }),
    password: emailChanged ? yup.string().required() : yup.string(),
  });

interface FormData {
  name: string;
  email?: string;
  password?: string;
  role?: UserRoles;
}

interface FormValidation {
  isValid: boolean;
  errors: Partial<FormData>;
}

const MemberForm: React.FC<MemberFormProps> = ({
  roleOptions,
}: MemberFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedUser = useSelector(selectSelectedUser);
  const propertyId = useSelector(selectCurrentPropertyId);
  const currentRole = useSelector(selectCurrentRole);

  const [form, setForm] = useState<FormData>({
    name: "",
    email: undefined,
    password: undefined,
    role: undefined,
  });
  const [value, setValue] = useState<Value>([]);
  const [validation, setValidation] = useState<FormValidation>({
    isValid: false,
    errors: {},
  });
  const [showModal, setShowModal] = useState(false);

  const selectedUserId = selectedUser?.id;
  const isInEditMode = !!selectedUser;
  const canEdit = useMemo(
    () =>
      roleOptions.find((option) => option.value === form.role) !== undefined,
    [form.role],
  );
  const hasChanges = useMemo(() => {
    if (!selectedUser) return true;
    const {
      email,
      profile: { name },
      roles,
    } = selectedUser;
    return !isEqual(
      { email, name, role: getMostImportantRole(roles) },
      { ...form, role: form.role },
    );
  }, [form]);
  const isHkMember = form?.role === UserRoles.HK_MEMBER;
  // HK members have no editable email field (it's forced to undefined and hidden),
  // so they can never change their email and must never be asked for a password.
  const emailChanged =
    isInEditMode && !isHkMember && form.email !== selectedUser?.email;
  const canSubmit = hasChanges && canEdit && validation.isValid;

  const validate = async () => {
    const result = await Form.validate(
      buildSchema(isInEditMode && emailChanged),
      form,
    );
    if (result === null) {
      setValidation({ isValid: true, errors: {} });
    } else {
      setValidation({ isValid: false, errors: result });
    }
  };

  const onShowDeleteModal = () => {
    setShowModal(true);
  };

  const onDeleteMember = () => {
    if (!selectedUserId) return;
    dispatch(deleteUser(selectedUserId));
    setShowModal(false);
  };

  const onSubmit = () => {
    if (!canSubmit) return;
    const { email, role, name, password } = form;

    if (isInEditMode) {
      dispatch(
        updateUser(
          {
            email,
            roles: role && [role],
            properties: propertyId ? [propertyId] : [],
            profile: { name },
            ...(emailChanged && password ? { password } : {}),
          },
          selectedUserId!,
        ),
      );
    } else {
      if (propertyId !== undefined && role !== undefined)
        dispatch(
          createUser({
            email,
            roles: [role],
            properties: [propertyId],
            profile: { name },
          }),
        );
    }
  };

  const setInitialRole = () => {
    switch (currentRole) {
      case UserRoles.ADMIN:
      case UserRoles.HOTEL_MANAGER:
      case UserRoles.HK_MANAGER:
        return UserRoles.HK_MEMBER;
      case UserRoles.TD_MANAGER:
        return UserRoles.TD_MEMBER;
      default:
        return undefined;
    }
  };

  const prefillForm = () => {
    if (selectedUser === undefined) {
      setForm({ role: setInitialRole(), email: undefined, name: "" });
      setValue([]);
      return;
    }

    const {
      email,
      profile: { name },
      roles,
    } = selectedUser;
    const mostImportantRole = getMostImportantRole(roles);

    setValue([
      mostImportantRole && {
        label: t(`roleNames.${mostImportantRole}`),
        value: mostImportantRole,
      },
    ] as Value);

    setForm({
      name,
      email: roles.includes(UserRoles.HK_MEMBER) ? undefined : email,
      password: undefined,
      role: mostImportantRole || setInitialRole(),
    });
  };

  useEffect(() => {
    prefillForm();
  }, [selectedUser]);

  useEffect(() => {
    validate();
  }, [form]);

  return (
    <div css={styles.container}>
      <div css={styles.form}>
        <h3 css={styles.title}>
          {selectedUserId ? t("team.detail") : t("team.addMember")}
        </h3>
        <Select
          value={value}
          options={roleOptions}
          onChange={(param) => {
            setForm({
              ...form,
              role: param.option?.value,
            });
            setValue(param.value);
          }}
          containerStyle={{
            width: "325px",
          }}
          props={{
            placeholder: t("search.function"),
            labelKey: "label",
            valueKey: "value",
            clearable: false,
            searchable: false,
          }}
        />
        <Input
          autoFocus
          label={t("common.name")}
          value={form.name}
          onChangeText={(text) => setForm({ ...form, name: text })}
          containerStyle={{
            marginTop: "10px",
            width: "100%",
          }}
          placeholder={t("team.inputName")}
        />
        {!isHkMember && (
          <>
            <Input
              label={t("login.self")}
              value={form.email}
              onChangeText={(text) =>
                setForm({ ...form, email: text.trim(), password: undefined })
              }
              containerStyle={{
                marginBottom: "20px 0",
                marginTop: "10px",
                width: "325px",
              }}
              placeholder={t("team.inputEmail")}
            />
            {isInEditMode && emailChanged && (
              <Input
                label={t("login.password")}
                labelAdornment={
                  <PopOver
                    placement={PLACEMENT.top}
                    content={
                      <div css={styles.popoverContent}>
                        {t("team.inputPasswordInfo")}
                      </div>
                    }
                    actionElement={
                      <span css={styles.infoIcon}>
                        <HiOutlineInformationCircle />
                      </span>
                    }
                  />
                }
                value={form.password}
                onChangeText={(text) => setForm({ ...form, password: text })}
                inputType="password"
                containerStyle={{
                  marginTop: "10px",
                  width: "100%",
                  paddingRight: "0 !important",
                }}
                placeholder={t("team.inputPassword")}
              />
            )}
            {!selectedUserId && (
              <Notification content={t("team.inputEmailInfo")} kind="info" />
            )}
          </>
        )}

        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          overrides={overrideButtonStyles({
            BaseButton: { marginTop: "32px" },
          })}
        >
          {isInEditMode ? t("common.saveChanges") : t("common.save")}
        </Button>
      </div>
      {selectedUserId && (
        <Button
          onClick={onShowDeleteModal}
          kind="secondary"
          overrides={overrideButtonStyles({
            BaseButton: ({ $theme, $disabled }) => ({
              margin: "24px",
              position: "absolute",
              bottom: 0,
              right: 0,
              ...generateButtonColors({
                $disabled,
                color: $theme.colors.negative,
              }),
            }),
          })}
          disabled={!canEdit}
        >
          {t("team.removeMember")}
        </Button>
      )}

      <Modal
        title={t("common.youSure")}
        buttonText={t("team.removeMember")}
        buttonAction={onDeleteMember}
        isOpen={showModal}
        setShowModal={setShowModal}
      >
        {t("team.deleteConfirmation", { value: selectedUser?.profile.name })}
      </Modal>
    </div>
  );
};

const styles = {
  container: css({
    flex: 1,
    justifyContent: "space-between",
    marginLeft: 16,
    marginTop: 24,
  }),
  form: css({
    width: 324,
  }),
  input: css({
    margin: "20px 0",
  }),
  title: css({
    paddingBottom: "20px",
  }),
  infoIcon: css({
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    fontSize: "1rem",
  }),
  popoverContent: css({
    padding: "8px 12px",
    maxWidth: "260px",
    fontSize: "0.8rem",
    color: theme.colors.contentPrimary,
    backgroundColor: theme.colors.mono100,
    borderRadius: "5px",
    whiteSpace: "normal",
  }),
};

export default MemberForm;
